/**
 * themePreference — the ONE decision table for "which theme does this person get".
 *
 * design-system PR-1 (2026-09-23). Two readers must agree byte for byte on
 * what a stored `ui_theme` value means: the React host (src/context/AppContext.tsx,
 * which owns the preference and sets `data-theme` on <html>) and the inline
 * pre-paint script in public/index.html, which cannot import anything and runs
 * before the bundle so the first frame is never the wrong theme. This module is
 * the table the host reads; tests/unit/themeMechanism.test.ts drives the inline
 * script through the same inputs and requires the same answers, so the two can
 * never drift apart silently.
 *
 * The stored value's history, all accepted:
 *   - the legacy boolean `darkMode` that AppContext wrote as JSON (`true` /
 *     `false`) since the field existed, with no toggle and no reader;
 *   - the preference as a JSON string (`"light"` / `"dark"` / `"system"`) —
 *     saveToLocalStorage JSON-encodes everything;
 *   - the bare words, for a hand-edited value.
 * Anything else is the default. Firebase-free and DOM-free on purpose.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** The three choices, in the order the toggle offers them. */
export const THEME_PREFERENCES: ReadonlyArray<ThemePreference> = ['light', 'dark', 'system'];

/**
 * D-3: `system` becomes the default at PR-6 (dark general availability). Until
 * then a person with no stored preference gets the light theme — today's look.
 */
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light';

export const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** What a raw localStorage `ui_theme` value means. Never throws. */
export function parseStoredThemePreference(raw: string | null | undefined): ThemePreference {
  if (raw === null || raw === undefined || raw === '') return DEFAULT_THEME_PREFERENCE;
  let value: unknown = raw;
  try {
    value = JSON.parse(raw);
  } catch (_e) {
    value = raw;
  }
  if (value === true || value === 'dark') return 'dark';
  if (value === false || value === 'light') return 'light';
  if (value === 'system') return 'system';
  return DEFAULT_THEME_PREFERENCE;
}

/** The theme a preference lands on, given what the OS says. */
export function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

type MatchMediaLike = { matchMedia?: (query: string) => { matches: boolean } };

/** Does the OS ask for dark? False wherever matchMedia is missing or throws (tests, old engines). */
export function systemPrefersDark(win: MatchMediaLike | undefined): boolean {
  try {
    return Boolean(win && typeof win.matchMedia === 'function' && win.matchMedia(SYSTEM_DARK_QUERY).matches);
  } catch (_e) {
    return false;
  }
}
